import { Injectable, NgZone, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { UserInactivityDialogComponent } from '@shared/components/user-inactivity-dialog/user-inactivity-dialog.component';
import { logoutAndRedirect } from '@shared/store/auth/auth.action';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { Subscription, asyncScheduler, fromEvent, mergeAll, scheduled, take } from 'rxjs';
import { clearTimeout, setTimeout } from 'worker-timers';

@Injectable({
  providedIn: 'root',
})
export class UserInactivityService {
  private readonly ngZone = inject(NgZone);
  private readonly dialogService = inject(DialogService);
  private readonly store = inject(Store);
  private ref: DynamicDialogRef | undefined;

  // The maximum delay for setTimeout in some JS environments (approx. 24.8 days).
  // Using a value larger than this can cause the timer to fire immediately.
  private readonly MAX_SET_TIMEOUT_DELAY = 2147483647;

  private userActivityEvents = ['mousemove', 'mousedown', 'keypress', 'touchstart', 'scroll'];
  private activitySubscription!: Subscription;
  private logoutTimer!: number;

  private timeoutInMinutes: number;
  private readonly CONFIRMATION_TIMEOUT_MINUTES = 2;

  startMonitoring(timeoutInMinutes: number): void {
    this.timeoutInMinutes = timeoutInMinutes;
    this.ngZone.runOutsideAngular(() => {
      const activityObservables = this.userActivityEvents.map((event) => fromEvent(document, event));
      const allEvents$ = scheduled(activityObservables, asyncScheduler).pipe(mergeAll());
      this.activitySubscription = allEvents$.subscribe(() => this.resetTimer());
    });
    this.resetTimer();
  }

  private resetTimer(): void {
    clearTimeout(this.logoutTimer);
    const timeoutMs = Math.min((this.timeoutInMinutes - this.CONFIRMATION_TIMEOUT_MINUTES) * 60 * 1000, this.MAX_SET_TIMEOUT_DELAY);
    this.logoutTimer = setTimeout(() => {
      this.ngZone.run(() => this.showConfirmationModal());
    }, timeoutMs);
  }

  private showConfirmationModal(): void {
    this.stopMonitoring();
    this.ref = this.dialogService.open(UserInactivityDialogComponent, {
      data: { countdownSeconds: this.CONFIRMATION_TIMEOUT_MINUTES * 60 },
    });
    this.ref.onClose.pipe(take(1)).subscribe((logout) => {
      if (logout) {
        this.handleLogout();
      } else {
        this.startMonitoring(this.timeoutInMinutes);
      }
    });
  }

  handleLogout(): void {
    this.store.dispatch(logoutAndRedirect());
  }

  stopMonitoring(): void {
    this.activitySubscription?.unsubscribe();
    clearTimeout(this.logoutTimer);
  }
}
